// Talent Management > Opt-out requests: talents who ASKED to stop being contacted.
//
// This is the queue, not the suppression. The tab beside it ("Opt-outs") IS the
// suppression list: ids an operator types in, hard-filtered out of every search. This
// page is what feeds it. When a talent tells STERLING to stop, `register_opt_out`
// records a row here (migration 0120) and pages CS; nothing is suppressed until someone
// hits "Add to opt-out list" on a row.
//
// That gap is deliberate and it is the reason this page exists at all. Before it, the
// request produced a Slack alert and a log line and nothing else, so an afternoon nobody
// watched the channel meant the request was simply gone and the person stayed in the
// pool. Keeping suppression a human act while making the REQUEST durable is the whole
// design: the row is the reminder that outlives the notification.
//
// `suppressed` is read live off the opt-out list, not inferred from `handledAt`, so a
// talent later removed from that list shows as un-suppressed again here and the row can
// be actioned a second time. That is what makes this a worklist rather than a log of
// past clicks.
import {
  Badge,
  Button,
  ConfirmDialog,
  SortableTh,
  TableExport,
  TableFilters,
  type FilterColumn,
  useColumnFilters,
  usePagedRows,
  useTableSort,
} from "@recruiter/shared";
import type { TalentOptoutRequestRow } from "@recruiter/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { AreaBoundary } from "../components/area-gate";
import { SectionTabs, TALENT_TABS } from "../components/section-tabs";
import { ShowMoreRow } from "../components/show-more-row";
import { EmptyState, TableSkeleton } from "../components/states";
import { ExplorerProjectLink, ExplorerTalentLink } from "../components/explorer-link";
import { formatDateTime } from "../lib/format";
import { projectTitle } from "../lib/project-title";
import { resolveOptoutRequest } from "../lib/api";
import { adminKeys, talentOptoutRequestsQuery } from "../lib/queries";
import { useWriteAccess } from "../lib/use-access";
import "./searches.css";
import "./talent-declines.css";
import "./talent-lists.css";

/** Sort accessors are value EXTRACTORS (lib/table-sort.ts). Status orders open rows
 *  first because that is the order the page is worked in: `0` for an outstanding
 *  request, `1` once it has been actioned. */
const REQUEST_SORT = {
  asked: (r: TalentOptoutRequestRow) => r.createdAt ?? null,
  talent: (r: TalentOptoutRequestRow) => r.talentName ?? r.sellerId,
  status: (r: TalentOptoutRequestRow) => (r.suppressed ? 1 : 0),
  project: (r: TalentOptoutRequestRow) =>
    r.project.id
      ? projectTitle({ id: r.project.id, name: r.project.name, summary: r.project.summary })
      : null,
} as const;

/** One control, and it is the only column here that repeats: everything else is one
 *  talent, one date, one message. CLOSED so both halves stay listed at zero, which
 *  makes an empty queue read as "nothing outstanding" rather than as a missing filter.
 *
 *  Keyed on `suppressed`, NOT on `handledAt`, for the same reason the page is: what an
 *  operator needs to know is whether this person is off the pool right now. */
const REQUEST_FILTERS: readonly FilterColumn<TalentOptoutRequestRow>[] = [
  {
    key: "state",
    label: "Filter by state",
    allLabel: "Any state",
    value: (r) => (r.suppressed ? "done" : "open"),
    options: [
      { value: "open", label: "Still contactable" },
      { value: "done", label: "On the opt-out list" },
    ],
  },
];

export function TalentOptoutRequestsPage() {
  const qc = useQueryClient();
  const requests = useQuery(talentOptoutRequestsQuery);
  const [pending, setPending] = useState<TalentOptoutRequestRow | null>(null);
  // Reading this queue is `talent_management` at `read`; suppressing somebody from
  // it is `full` (migration 0120). A read-only holder is meant to WATCH the
  // worklist, so the page loads for them and only the action column is dropped.
  const { canWrite: canResolve, known: accessKnown } = useWriteAccess("talent_management");

  const resolve = useMutation({
    mutationFn: resolveOptoutRequest,
    onSuccess: () => {
      // Both lists move: this queue stamps the row, and the suppression list beside
      // it gains a seller. Invalidating only the first would leave the Opt-outs tab
      // showing a stale list until it happened to refetch.
      void qc.invalidateQueries({ queryKey: adminKeys.talentOptoutRequests });
      void qc.invalidateQueries({ queryKey: adminKeys.talentOptout });
    },
  });

  const rows = useMemo<TalentOptoutRequestRow[]>(
    () => requests.data?.requests ?? [],
    [requests.data],
  );
  const filters = useColumnFilters(rows, REQUEST_FILTERS, {
    restore: "talent-optout-requests/filters",
  });
  const sorted = useTableSort(filters.rows, REQUEST_SORT, null, {
    restore: "talent-optout-requests/sort",
  });
  const paged = usePagedRows(sorted.rows, { restore: "talent-optout-requests/window" });

  if (requests.isError) {
    return (
      <div className="ad-page">
        <AreaBoundary
          area="talent_management"
          error={requests.error}
          onRetry={() => void requests.refetch()}
        />
      </div>
    );
  }

  const openCount = requests.data?.openCount ?? 0;

  return (
    <div className="ad-page">
      <header className="ad-page__head">
        <h1 className="ad-page__title">Opt-out requests</h1>
        {/* The second sentence names a CONTROL, so it waits for the grant query and
            then says whose control it is. Telling a read-only holder to press a
            button that is not drawn for them is worse than saying nothing. */}
        <p className="ad-page__sub">
          Talents who asked us to stop contacting them. Nothing here is suppressed automatically.
          {accessKnown
            ? canResolve
              ? ' "Add to opt-out list" is what actually takes someone out of every future search.'
              : " Somebody with Full access has to add them to the opt-out list before a search stops surfacing them."
            : null}
        </p>
      </header>

      <SectionTabs label="Talent Management sections" tabs={TALENT_TABS} />

      <div className="rc-toolbar sr-toolbar">
        <TableFilters controls={filters.controls} active={filters.active} onClear={filters.clear} />
        {requests.data ? (
          <span className="rc-toolbar__count">
            {openCount.toLocaleString()} awaiting action of {rows.length.toLocaleString()}
          </span>
        ) : null}
      </div>

      {requests.isPending ? (
        <TableSkeleton columns={canResolve ? 6 : 5} />
      ) : rows.length === 0 ? (
        <EmptyState
          title="No opt-out requests"
          message="When a talent asks to stop being contacted, their request appears here."
        />
      ) : sorted.rows.length === 0 ? (
        <EmptyState title="No requests match" message="Try clearing a filter." />
      ) : (
        <>
          <div className="rc-table-wrap">
            <table className="rc-table">
              <TableExport name="optout-requests" label="opt-out requests" />
              <thead>
                <tr>
                  <SortableTh sortKey="asked" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Asked
                  </SortableTh>
                  <SortableTh sortKey="talent" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Talent
                  </SortableTh>
                  <SortableTh sortKey="project" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    Project
                  </SortableTh>
                  <th>They wrote</th>
                  <SortableTh sortKey="status" ariaSort={sorted.ariaSort} onToggle={sorted.toggle}>
                    State
                  </SortableTh>
                  {canResolve ? (
                    <th>
                      <span className="ad-visually-hidden">Action</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {paged.shown.map((row) => (
                  <tr key={row.id}>
                    <td>{formatDateTime(row.createdAt ?? null)}</td>
                    <td>
                      <ExplorerTalentLink sellerId={row.sellerId} className="ex-primary-link">
                        {row.talentName ?? row.sellerId}
                      </ExplorerTalentLink>
                      {row.talentName ? <span className="rc-cell-sub">{row.sellerId}</span> : null}
                    </td>
                    <td>
                      {row.project.id ? (
                        <ExplorerProjectLink projectId={row.project.id} className="ex-primary-link">
                          {projectTitle({
                            id: row.project.id,
                            name: row.project.name,
                            summary: row.project.summary,
                          })}
                        </ExplorerProjectLink>
                      ) : (
                        // The project is gone; the request stands. `project_id` is a
                        // weak reference on purpose (0120) so a deleted project can
                        // never erase the fact that somebody asked us to stop.
                        <span className="tm-unknown">Project deleted</span>
                      )}
                    </td>
                    <td>
                      {row.requestText ? (
                        // Verbatim, unnormalized: these are the talent's own words.
                        <span className="td-detail" title={row.requestText}>
                          {row.requestText}
                        </span>
                      ) : (
                        <span className="tm-unknown">No message recorded</span>
                      )}
                    </td>
                    <td>
                      {row.suppressed ? (
                        <Badge
                          tone="success"
                          size="sm"
                          title={
                            row.handledBy
                              ? `On the opt-out list. Resolved by ${row.handledBy}.`
                              : "On the opt-out list: excluded from every search."
                          }
                        >
                          Opted out
                        </Badge>
                      ) : (
                        <Badge
                          tone="warning"
                          size="sm"
                          title="Not suppressed yet: a search can still surface this talent."
                        >
                          Still contactable
                        </Badge>
                      )}
                    </td>
                    {canResolve ? (
                      <td>
                        {row.suppressed ? null : (
                          <Button
                            type="button"
                            variant="outline"
                            tone="neutral"
                            size="sm"
                            onClick={() => setPending(row)}
                          >
                            Add to opt-out list
                          </Button>
                        )}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ShowMoreRow remaining={paged.remaining} onClick={paged.showMore} />
        </>
      )}

      {/* Says what the TALENT will experience, not which column moves — the pattern
          every client-facing write on this console follows. */}
      <ConfirmDialog
        open={pending !== null}
        title="Add this talent to the opt-out list?"
        message={
          pending
            ? `${pending.talentName ?? pending.sellerId} will be excluded from every future search, and Mira will not contact them again. You can undo this on the Opt-outs tab.`
            : ""
        }
        confirmLabel="Add to opt-out list"
        busy={resolve.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return;
          resolve.mutate({ sellerId: pending.sellerId }, { onSettled: () => setPending(null) });
        }}
      />
    </div>
  );
}
